import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SendIcon } from '../components/icons.tsx';
import { Spinner, formatTime } from '../components/ui.tsx';

/**
 * A customer's own conversation, opened from a link in an email.
 *
 * Signed-in staff read conversations in Chats; this is the other side of the
 * same thread, and the person opening it has no account. The session uuid in
 * the URL is the capability — the same model the widget uses, and the same one
 * the WordPress build used, where both ids sat in the page URL.
 *
 * It talks only to the public endpoints and the websocket, so nothing here
 * needs a cookie and nothing here can reach another conversation.
 */

interface WireMessage {
  id: number;
  content: string;
  createdAt: string;
  user: { id: number | null; displayName: string };
  meta?: { system?: boolean };
  attachments?: { url: string; thumbnailUrl?: string; name?: string }[];
}

/**
 * Whose side of the conversation a message is on.
 *
 * There is no flag on the wire for this — the same rule the agent app and the
 * widget use is: no author and not a system notice means the customer wrote it.
 * A welcome or an auto-response has no author either, which is why the system
 * check has to be part of it.
 */
function fromCustomer(message: WireMessage): boolean {
  return message.user.id === null && !message.meta?.system;
}

interface ChatConfig {
  chat: { uuid: string; title: string; widgetColor: string };
}

const HEX = /^#[0-9a-fA-F]{6}$/;

export function ConversationPage(): React.JSX.Element {
  const { chatUuid = '', sessionUuid = '' } = useParams();

  const [config, setConfig] = useState<ChatConfig | null>(null);
  const [messages, setMessages] = useState<WireMessage[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'missing'>('loading');
  const [draft, setDraft] = useState('');
  const [connected, setConnected] = useState(false);

  const socket = useRef<WebSocket | null>(null);
  const bottom = useRef<HTMLDivElement>(null);

  const add = useCallback((message: WireMessage) => {
    setMessages((previous) =>
      // The socket can echo something the transcript already carried.
      previous.some((item) => item.id === message.id) ? previous : [...previous, message],
    );
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const [chatResponse, messageResponse] = await Promise.all([
          fetch(`/public/chats/${chatUuid}`),
          fetch(`/public/chats/${chatUuid}/sessions/${sessionUuid}/messages`),
        ]);

        if (!chatResponse.ok || !messageResponse.ok) {
          if (!cancelled) setStatus('missing');
          return;
        }

        const chat = (await chatResponse.json()) as ChatConfig;
        const transcript = (await messageResponse.json()) as { items: WireMessage[] };

        if (cancelled) return;

        setConfig(chat);
        setMessages(transcript.items);
        setStatus('ready');
      } catch {
        if (!cancelled) setStatus('missing');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [chatUuid, sessionUuid]);

  useEffect(() => {
    if (status !== 'ready') return;

    const url = new URL(window.location.href);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    url.pathname = '/ws';
    url.search = `?chat=${chatUuid}&session=${sessionUuid}`;

    const live = new WebSocket(url);
    socket.current = live;

    live.onopen = () => {
      setConnected(true);
    };
    live.onclose = () => {
      setConnected(false);
    };

    live.onmessage = (event: MessageEvent<string>) => {
      let payload: { type?: string; message?: WireMessage };

      try {
        payload = JSON.parse(event.data) as typeof payload;
      } catch {
        return;
      }

      // `message` is the other side writing; `ack` is the receipt for our own,
      // which the server sends instead of echoing it back to the sender.
      if ((payload.type === 'message' || payload.type === 'ack') && payload.message) {
        add(payload.message);
      }
    };

    return () => {
      live.close();
      socket.current = null;
    };
  }, [status, chatUuid, sessionUuid, add]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = (): void => {
    const content = draft.trim();
    if (!content || socket.current?.readyState !== WebSocket.OPEN) return;

    socket.current.send(
      JSON.stringify({
        type: 'message',
        content,
        clientId: `c-${String(Date.now())}`,
      }),
    );

    setDraft('');
  };

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Spinner className="h-6 w-6 text-brand-600" />
      </div>
    );
  }

  if (status === 'missing') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 text-center dark:bg-slate-950">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
            This conversation is not here
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            The link may have expired, or the conversation may have been deleted. Replying to the
            email still reaches them.
          </p>
        </div>
      </div>
    );
  }

  const accent = HEX.test(config?.chat.widgetColor ?? '') ? config!.chat.widgetColor : '#7958f8';

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 dark:bg-slate-950">
      <header className="px-5 py-4 text-white" style={{ backgroundColor: accent }}>
        <div className="mx-auto w-full max-w-2xl">
          <h1 className="text-base font-semibold">{config?.chat.title || 'Conversation'}</h1>
          <p className="text-xs opacity-80">
            {connected ? 'You can reply below' : 'Reconnecting…'}
          </p>
        </div>
      </header>

      <main className="mx-auto w-full max-w-2xl flex-1 space-y-3 px-5 py-6">
        {messages.map((message) => (
          <div
            key={message.id}
            className={fromCustomer(message) ? 'flex justify-end' : 'flex justify-start'}
          >
            <div className="max-w-[80%]">
              {!fromCustomer(message) && message.user.displayName ? (
                <span className="mb-0.5 block text-xs text-slate-500 dark:text-slate-400">
                  {message.user.displayName}
                </span>
              ) : null}

              <div
                className={
                  fromCustomer(message)
                    ? 'rounded-2xl px-3.5 py-2 text-sm text-white'
                    : 'rounded-2xl bg-white px-3.5 py-2 text-sm text-slate-800 shadow-sm dark:bg-slate-800 dark:text-slate-100'
                }
                style={fromCustomer(message) ? { backgroundColor: accent } : undefined}
              >
                {message.content ? <p className="whitespace-pre-wrap">{message.content}</p> : null}

                {(message.attachments ?? []).map((attachment) => (
                  <a
                    key={attachment.url}
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1.5 block"
                  >
                    <img
                      src={attachment.thumbnailUrl ?? attachment.url}
                      alt={attachment.name ?? 'Attachment'}
                      className="max-h-56 rounded-lg"
                    />
                  </a>
                ))}
              </div>

              <span className="mt-0.5 block px-1 text-[11px] text-slate-400">
                {formatTime(message.createdAt)}
              </span>
            </div>
          </div>
        ))}

        <div ref={bottom} />
      </main>

      <footer className="sticky bottom-0 border-t border-slate-200 bg-white px-5 py-3 dark:border-slate-800 dark:bg-slate-900">
        <form
          className="mx-auto flex w-full max-w-2xl items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            send();
          }}
        >
          <textarea
            className="lg-input max-h-32 min-h-11 flex-1 resize-none py-2.5"
            placeholder="Write a reply…"
            rows={1}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                send();
              }
            }}
          />

          <button
            type="submit"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-white disabled:opacity-50"
            style={{ backgroundColor: accent }}
            disabled={!draft.trim() || !connected}
          >
            <SendIcon className="h-4 w-4" />
            <span className="sr-only">Send</span>
          </button>
        </form>

        <p className="mx-auto mt-2 w-full max-w-2xl text-center text-[11px] text-slate-400">
          <a href="/privacy" className="hover:underline">
            Privacy
          </a>
        </p>
      </footer>
    </div>
  );
}
